import { Component, Input, ViewChild, ElementRef, OnDestroy, OnInit, NgZone } from '@angular/core';
import { CommonService } from 'src/app/services/Common-function/common.service';
import { PlayerService } from 'src/app/services/music-player/player.service';
import { BrowserStorageService } from 'src/app/services/browser-storage/browser-storage.service';
import { Ga4trackingService } from 'src/app/services/ga4Tracking/ga4tracking.service';

export interface AudioAttachment {
  type: 'recording' | 'file' | 'url';
  name: string;
  fingerprint: boolean;
  /** Raw audio bytes for recording/upload. Absent for URL attachments. */
  blob?: Blob;
  /**
   * Raw base64 audio (no data-url prefix), carried over when this attachment
   * was rehydrated from a search payload rather than freshly recorded/uploaded —
   * the original Blob doesn't survive that round-trip, but the bytes still need
   * to reach the next search request so the attachment isn't silently dropped.
   */
  base64?: string;
}

@Component({
  selector: 'ipm-magic-search-audio-modal',
  templateUrl: './magic-search-audio-modal.component.html',
  styleUrls: ['./magic-search-audio-modal.component.scss'],
})
export class MagicSearchAudioModalComponent implements OnInit, OnDestroy {
  @Input() fingerprint = false;
  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

  @Input() activeTab: 'record' | 'upload' | 'url' ;
  recording = false;
  recorded = false;
  seconds = 0;
  private timer: any;
  private mediaRecorder: MediaRecorder | null = null;
  private recordedChunks: BlobPart[] = [];
  private recordedBlob: Blob | null = null;
  private recordedMime = 'audio/webm';

  // ---- Visualizer ----
  /** Number of bars rendered in the strip. Sized so 3px bars + 3px gaps fill
   *  the full ~412px inner strip width of a 460px modal. */
  readonly BAR_COUNT = 68;
  /** Bar heights normalised to 0..1. Length === BAR_COUNT. */
  levels: number[] = [];
  /** Visualizer state drives styling (gray dummy vs live vs frozen capture). */
  vizState: 'idle' | 'recording' | 'recorded' = 'idle';

  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private analyserData: Uint8Array | null = null;
  private rafId: number | null = null;
  private smoothVolume = 0;
  private lastSampleTs = 0;
  /** Running peak RMS used for adaptive gain. Decays slowly so the strip
   *  auto-calibrates to whatever mic the user has. */
  private runningPeak = 0.05;
  /** Push a new bar roughly every this many ms while recording. */
  private readonly SAMPLE_INTERVAL_MS = 70;

  // upload
  fileName = '';
  private uploadedFile: File | null = null;

  // url
  urlValue = '';

  constructor(
    public commonService: CommonService,
    private ngZone: NgZone,
    private playerService: PlayerService,
    private browserService : BrowserStorageService,
    private ga4Tracking : Ga4trackingService
  ) {}

  ngOnInit(): void {
    this.reset();
  }

  cancel(): void {
    this.stopRecording();
    this.commonService.closingPopupWithSendData(null);
  }

  setTab(tab: 'record' | 'upload' | 'url'): void {
    this.activeTab = tab;
  }

  // ---- Record ----
  async startRecording(): Promise<void> {
    try {
      this.playerService.pause();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.recordedChunks = [];
      this.recordedBlob = null;
      this.mediaRecorder = new MediaRecorder(stream);
      this.recordedMime = this.mediaRecorder.mimeType || 'audio/webm';
      this.mediaRecorder.ondataavailable = (e: BlobEvent) => {
        if (e.data && e.data.size > 0) this.recordedChunks.push(e.data);
      };
      this.mediaRecorder.onstop = () => {
        this.recordedBlob = new Blob(this.recordedChunks, { type: this.recordedMime });
        // Mark as recorded only after the blob is fully assembled, so submit() never
        // races ahead with a null blob.
        this.recorded = true;
      };
      this.mediaRecorder.start();
      this.recording = true;
      this.recorded = false;
      this.seconds = 0;
      this.timer = setInterval(() => this.seconds++, 1000);

      // Start visualizer on the same MediaStream — analyser-only, recording is untouched.
      this.startVisualizer(stream);
    } catch {
      alert('Microphone access denied.');
    }
  }

  stopRecording(): void {
    // Stop the visualizer first so we don't paint a flatlined frame after the
    // mic tracks end, and we keep whatever bars were captured (frozen state).
    this.stopVisualizer();

    if (this.mediaRecorder) {
      this.mediaRecorder.stop();
      this.mediaRecorder.stream.getTracks().forEach(t => t.stop());
      this.mediaRecorder = null;
    }
    clearInterval(this.timer);
    if (this.recording) {
      this.recording = false;
      this.vizState = this.hasAnyLevel() ? 'recorded' : 'idle';
      // `recorded` is flipped to true inside the MediaRecorder.onstop handler
      // once `recordedBlob` is ready.
    }
  }

  // ---- Upload ----
  onFileSelect(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      this.fileName = file.name;
      this.uploadedFile = file;
    }
  }

  triggerFileInput(): void {
    this.fileInput?.nativeElement.click();
  }

  // ---- Submit ----
  get canSubmit(): boolean {
    if (this.activeTab === 'record') return this.recorded;
    if (this.activeTab === 'upload') return !!this.fileName;
    return !!this.urlValue.trim();
  }

  submit(): void {
    if (!this.canSubmit) return;
    let attachment: AudioAttachment;
    let eventName = '';
    let attributes = {
      label : 'Engagement',
      action : 'upload',
      category : 'magic search',
      userId :   this.browserService.getUserId(),
      userRole : this.browserService.getCurrentRoles()
    }
    if (this.activeTab === 'record') {
      eventName = 'blob_upload_successful'
      const ext = this.recordedMime.includes('webm') ? 'webm' : (this.recordedMime.includes('mp4') ? 'm4a' : 'wav');
      attachment = {
        type: 'recording',
        name: `hum_${this.fmt(this.seconds)}.${ext}`,
        fingerprint: this.fingerprint,
        blob: this.recordedBlob ?? undefined,
      };
    } else if (this.activeTab === 'upload') {
      eventName = 'file_upload_successful'
      attachment = {
        type: 'file',
        name: this.fileName,
        fingerprint: this.fingerprint,
        blob: this.uploadedFile ?? undefined,
      };
    } else {
      eventName = 'url_upload_successful'
      attachment = { type: 'url', name: this.urlValue.trim(), fingerprint: this.fingerprint };
    }

    this.ga4Tracking.sendEvent(eventName , attributes)
    this.stopRecording();
    this.commonService.closingPopupWithSendData(attachment);
  }

  fmt(s: number): string {
    return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  }

  trackByIndex(index: number): number {
    return index;
  }

  // ---- Visualizer internals ----

  /** Build an idle "dummy" waveform — a soft hill with tiny jitter — so the strip
   *  never looks empty before the user hits Record. */
  private buildIdleLevels(): number[] {
    const arr: number[] = new Array(this.BAR_COUNT);
    const mid = (this.BAR_COUNT - 1) / 2;
    for (let i = 0; i < this.BAR_COUNT; i++) {
      // Symmetric hill (cosine-ish), kept low so it reads as muted/inactive.
      const dist = Math.abs(i - mid) / mid; // 0 at center, 1 at edges
      const hill = Math.cos((dist * Math.PI) / 2); // 1 → 0
      const jitter = 0.85 + Math.random() * 0.3;
      arr[i] = Math.max(0.06, hill * 0.18 * jitter);
    }
    return arr;
  }

  private startVisualizer(stream: MediaStream): void {
    // Reset to a flat, near-zero waveform so live samples scroll in cleanly.
    this.levels = new Array(this.BAR_COUNT).fill(0.04);
    this.vizState = 'recording';
    this.smoothVolume = 0;
    this.lastSampleTs = 0;
    // Seed the running peak low enough that the very first words read tall
    // — it'll self-correct upward within a few samples if the mic is hot.
    this.runningPeak = 0.05;

    try {
      const Ctor: typeof AudioContext =
        (window as any).AudioContext || (window as any).webkitAudioContext;
      this.audioContext = new Ctor();
      const source = this.audioContext.createMediaStreamSource(stream);
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 256;
      this.analyser.smoothingTimeConstant = 0.4;
      source.connect(this.analyser);
      this.analyserData = new Uint8Array(this.analyser.frequencyBinCount);
    } catch {
      // If the AudioContext can't be created we just skip visuals; recording still works.
      this.analyser = null;
      this.analyserData = null;
    }

    // Run the animation loop outside Angular so 60fps frames don't trigger
    // change detection on the whole modal. We `markForCheck` ourselves on each
    // sample tick (which is much less frequent than rAF).
    this.ngZone.runOutsideAngular(() => {
      const tick = (ts: number) => {
        if (this.vizState !== 'recording') return;
        this.sampleFrame(ts);
        this.rafId = requestAnimationFrame(tick);
      };
      this.rafId = requestAnimationFrame(tick);
    });
  }

  private sampleFrame(ts: number): void {
    if (!this.analyser || !this.analyserData) return;

    // Read time-domain data and compute RMS.
    this.analyser.getByteTimeDomainData(this.analyserData as any);
    let sum = 0;
    for (let i = 0; i < this.analyserData.length; i++) {
      const sample = (this.analyserData[i] - 128) / 128;
      sum += sample * sample;
    }
    const rms = Math.sqrt(sum / this.analyserData.length);
    // Smooth so the strip feels fluid instead of jittery.
    this.smoothVolume += (rms - this.smoothVolume) * 0.25;

    if (ts - this.lastSampleTs < this.SAMPLE_INTERVAL_MS) return;
    this.lastSampleTs = ts;

    // Adaptive gain: track running peak so any mic (laptop, headset, USB) fills
    // the strip. Peak rises instantly with louder samples and decays slowly so
    // brief silences between words don't spike the next bar.
    if (this.smoothVolume > this.runningPeak) {
      this.runningPeak = this.smoothVolume;
    } else {
      this.runningPeak = Math.max(0.04, this.runningPeak * 0.985);
    }

    // Normalise against running peak, then apply a sqrt curve for perceptual
    // scaling — lifts quiet samples without crushing loud ones. Floor keeps a
    // visible baseline tick during silences; ceiling caps at 95% so the very
    // top of the strip is reserved for genuine peaks.
    const norm = this.smoothVolume / this.runningPeak;
    const level = Math.min(0.95, Math.max(0.08, Math.sqrt(norm) * 0.92));

    // Shift left, push right (scrolling history).
    const next = this.levels.slice(1);
    next.push(level);
    this.levels = next;

    // Re-enter Angular only on sample ticks (~14fps), not every rAF — the
    // ngZone.run() alone is enough to trigger CD with default strategy.
    this.ngZone.run(() => {});
  }

  private stopVisualizer(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.audioContext) {
      try { this.audioContext.close(); } catch { /* noop */ }
      this.audioContext = null;
    }
    this.analyser = null;
    this.analyserData = null;
  }

  private hasAnyLevel(): boolean {
    return this.levels.some(l => l > 0.08);
  }

  private reset(): void {
    if (!this.activeTab) {
      this.activeTab = 'record';
    }
    this.recording = false;
    this.recorded = false;
    this.seconds = 0;
    this.fileName = '';
    this.uploadedFile = null;
    this.recordedChunks = [];
    this.recordedBlob = null;
    this.urlValue = '';
    this.levels = this.buildIdleLevels();
    this.vizState = 'idle';
  }  
  /**
   * Pause the beat player if it's currently playing. Used before mic/audio
   * capture so the two audio sources don't overlap. No-op when nothing is
   * playing — uses the mirrored flag on CommonService to avoid a fresh
   * subscription on every trigger.
   */
  private pauseBeatPlayerIfPlaying(): void {
    if (this.commonService.isMusicPlaying$.value) {
      this.playerService.pause();
    }
  }

  ngOnDestroy(): void {
    this.stopRecording();
  }
}
